/**
 * ResultView.tsx
 * ---------------------------------------------------------------------------
 * Admin copy of espy-dashboard's result-v2 ResultView (kept separate on
 * purpose, no shared code). Differences from the user UI:
 *  - always renders the `data` prop (ResultPageV2 fetches /request-monitor/:id
 *    and polls it while running); no viewResult store, no Dialog footer;
 *  - no "Continue the investigation" pivots or "Extract entities": an admin
 *    viewing someone's result must not launch billed searches as that user;
 *  - header adds the admin bits: owner link, DELETED label, JSON download.
 *
 * Structure (identical across every workflow):
 *   header card   — subject, verdict pill, answer line, meta, actions
 *   main column   — key facts strip, then the workflow-specific template
 *   sticky rail   — pivot suggestions and the IRBI insight slot
 */
import * as React from 'react'
import {
  Box, ClickAwayListener, Dialog, IconButton, Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useNavigate } from 'react-router-dom'
import { SearchResult } from '../../store/searcher/reducer'
import { groupProviders, ProviderSummary } from './adapters/providers'
import { resolveTemplate, resolveTemplateId, titleFor } from './registry'
import { buildSummary } from './adapters/summary'
import { InProgress, ProgressToast, useTokens } from './ui/primitives'
import { initials, radii , TYPO_BLOCK_FIX } from './tokens'
import { getNameVerificationCriteria } from './vendor/string'

type Props = {
  onClose?: () => void
  isMobile?: boolean
  /** render inline (a route) instead of inside a Dialog */
  inline?: boolean
  /** optional real Google map, forwarded to the geo and scene templates */
  mapSlot?: React.ReactNode
  /**
   * Explicit result to render. Used by the standalone /result/:id route, which
   * fetches one row by id into local state instead of going through
   * `toggleViewResult`. When omitted the `getViewResult` selector is used, so
   * the popup usage in PagePrivate is unaffected.
   */
  data?: SearchResult | null
  /**
   * Blurs every photo in the rendered template — used for the "View a
   * sample result" preview (SearchConsole.tsx), which shows realistic but
   * invented photos (stock avatars from i.pravatar.cc) alongside invented
   * names. Left unblurred those read as real people at a glance; blurring
   * keeps the layout/behaviour demo honest about being a mockup. Real
   * results never pass this.
   */
  blurPhotos?: boolean
  /** Admin header actions (owner link, DELETED label, downloads), rendered next to the close button. */
  adminActions?: React.ReactNode
  /** Where "Back" goes; defaults to the previous page. */
  onBack?: () => void
  backLabel?: string
}

const fmtDate = (v?: string) => {
  if (!v) return ''
  const d = new Date(v)
  return isNaN(d.getTime()) ? String(v) : d.toLocaleString()
}

export default function ResultView({ onClose, isMobile = false, inline = false, mapSlot, data, blurPhotos = false, adminActions, onBack, backLabel = 'Back' }: Props) {
  const theme = useTheme()
  const tk = useTokens()
  const navigate = useNavigate()
  const narrow = useMediaQuery('(max-width:1180px)') || isMobile
  // Portrait phone only — the header card's meta collapses to just the
  // technical fields (criteria/type/date/status), dropping the harvested
  // "answer" facts line and the resolved-subject-name item, which is more
  // than a small screen has room for anyway.
  const mobilePortrait = useMediaQuery(theme.breakpoints.down('sm'))
  // Tracks whether the header's own close button is currently on screen —
  // the floating fixed close button below only renders once it isn't, so
  // there's never two at once, but the header's button (the "real" one)
  // stays reachable the normal way whenever it's already visible. Runs on
  // every screen size (not just phone) — a long result is a long scroll
  // back to the header on desktop/tablet too.
  //
  // A callback ref rather than useRef + useEffect(..., []) — this view can
  // render `null` for a render or two while `apiData` is still loading (see
  // the `if (!apiData) return null` below), and a plain mount-effect with
  // an empty dep array only runs once, right after the *first* commit. If
  // that first commit was one of the null ones, the header button (and its
  // ref) wouldn't exist yet, the observer would never get attached, and
  // `headerCloseVisible` would be stuck at its initial `true` forever —
  // which is exactly "the floating button never appears" bug this fixes. A
  // callback ref instead fires precisely when the DOM node itself shows up
  // (or goes away), independent of which render that happens to be.
  // Long criteria (e.g. a full crypto wallet address) gets ellipsis-truncated
  // in the H1 below; tapping/clicking it reveals the untruncated text as a
  // controlled Tooltip, closed again by clicking elsewhere — same
  // ClickAwayListener + controlled Tooltip pattern as RenderTableBody.tsx's
  // HintIconButton.
  const [criteriaHintOpen, setCriteriaHintOpen] = React.useState(false)
  const [headerCloseVisible, setHeaderCloseVisible] = React.useState(true)
  const headerCloseObserverRef = React.useRef<IntersectionObserver | null>(null)
  const headerCloseRef = React.useCallback((el: HTMLButtonElement | null) => {
    headerCloseObserverRef.current?.disconnect()
    headerCloseObserverRef.current = null
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => setHeaderCloseVisible(entry.isIntersecting),
      { threshold: 0 },
    )
    observer.observe(el)
    headerCloseObserverRef.current = observer
  }, [])
  // The floating close button below needs to sit a fixed 35px from the
  // Dialog's own vertical scrollbar — not from the viewport edge, since the
  // scrollbar's actual width varies (OS, browser, overlay vs. classic,
  // mobile often 0) and a hardcoded `right` either overlapped it or left an
  // inconsistent gap depending on device. Paper (the Dialog's scrolling
  // element, since it's `fullScreen`) reports that width directly:
  // offsetWidth includes the scrollbar, clientWidth excludes it.
  const paperElRef = React.useRef<HTMLDivElement | null>(null)
  const [scrollbarWidth, setScrollbarWidth] = React.useState(0)
  const measureScrollbar = React.useCallback(() => {
    const el = paperElRef.current
    if (!el) return
    setScrollbarWidth(el.offsetWidth - el.clientWidth)
  }, [])
  const paperRef = React.useCallback((el: HTMLDivElement | null) => {
    paperElRef.current = el
    measureScrollbar()
  }, [measureScrollbar])
  // Re-measure whenever the button is about to appear (content/scrollbar
  // presence can change as data loads) and on window resize.
  React.useLayoutEffect(() => {
    measureScrollbar()
  }, [measureScrollbar, headerCloseVisible])
  React.useEffect(() => {
    window.addEventListener('resize', measureScrollbar)
    return () => window.removeEventListener('resize', measureScrollbar)
  }, [measureScrollbar])
  const apiData = data ?? null

  const close = onClose ?? (() => undefined)

  const providers = React.useMemo(() => (apiData ? groupProviders(apiData) : null), [apiData])
  const Template = React.useMemo(() => resolveTemplate(apiData), [apiData])
  const summary = React.useMemo(() => (apiData ? buildSummary(resolveTemplateId(apiData), apiData) : null), [apiData])
  // A harvested photo URL can still be dead (expired signed CDN link, 404,
  // ...) — only discoverable once the browser actually tries to load it, same
  // "only found on load" problem PersonTemplate/NameScanTemplate's own Photos
  // tiles solve with a broken-key set. Tracks failed URLs from `summary.photos`
  // so the header tile below can fall through to the next candidate instead
  // of being stuck showing a broken-image icon.
  const [brokenHeaderPhotos, setBrokenHeaderPhotos] = React.useState<Set<string>>(new Set())
  const headerPhoto = (summary?.photos || []).find((p) => !brokenHeaderPhotos.has(p))

  if (!apiData) return null

  const row: any = apiData
  const title = titleFor(apiData)
  // At least one provider has already returned data even though the search
  // as a whole is still 'progress' — in that case the template renders that
  // partial data right away (below) instead of being hidden behind the
  // full-panel InProgress state, and the radar moves into a small floating
  // ProgressToast instead so the user isn't left staring at a spinner while
  // real results are already sitting on screen.
  const hasPartialData = !!(providers && providers.withData.length > 0)
  const isImageCriteria = typeof row.criteria === 'string' && row.criteria.startsWith('data:image')
  const rawCriteria = isImageCriteria ? 'uploaded image' : row.criteria
  // lookup_phone_name_verification's criteria is a raw {"phoneNumber","name"}
  // JSON string (see RenderTableBody.tsx's own use of this same helper) —
  // shown as-is it'd be unreadable JSON in the header's H1.
  const criteria = row.type === 'lookup_phone_name_verification' && rawCriteria
    ? getNameVerificationCriteria(rawCriteria, 60)
    : rawCriteria

  const body = (
    <Box
      sx={{
        background: inline ? 'transparent' : tk.bg, p: inline ? 0 : '18px 20px 40px', minHeight: inline ? 0 : '100%', ...TYPO_BLOCK_FIX,
        // Dialog presentation only — the `inline` route already sits inside
        // PrivatePage's own layout/footer, so it keeps normal block flow.
        // Flex column + `flex: 1` on the body grid below lets that grid grow
        // to fill whatever's left above the footer, and `fill` on ResCard
        // (see NoData) grows a short empty-state panel to match — so a
        // "No data returned" page has the footer pinned at the bottom
        // instead of sitting right under a small fixed-height panel.
        ...(inline ? {} : { display: 'flex', flexDirection: 'column' }),
        // Scoped to `img` rather than a specific component so it catches
        // every photo across every template (Photo, avatars, face-match
        // thumbnails, scene photos) without each one needing to know it
        // might be rendering sample data. `[data-no-blur]` opts a subtree
        // out — MapCard's live Google Map (../ui/MapCard.tsx) marks itself
        // this way, since its tiles are <img> elements too but aren't a
        // photo of anyone; blurring them just made the map unreadable.
        ...(blurPhotos
          ? { '& img': { filter: 'blur(9px)', transform: 'scale(1.03)' }, '& [data-no-blur] img': { filter: 'none', transform: 'none' } }
          : {}),
      }}
    >
      {/* "Back to all searches" — shown in both presentations. On the
          standalone /result/:id page (`inline`) this is a plain navigation.
          Inside the Dialog popup (opened from the results table via
          `toggleViewResult`), it also closes the dialog first — otherwise
          the Dialog's own Modal (much higher z-index than the page behind
          it) would just sit open over "/" instead of actually going back to
          the table. The console lives at "/" (not "/searcher-v2" — see
          `runPivot`'s own comment above), same target every pivot-console
          handoff uses. */}
      <Box
        component="button"
        type="button"
        onClick={() => { if (!inline) close(); if (onBack) onBack(); else navigate(-1) }}
        sx={{
          display: 'inline-flex', alignItems: 'center', gap: '6px', mt: '-8px', mb: '10px',
          border: 'none', background: 'none', cursor: 'pointer', fontFamily: 'inherit',
          fontSize: 12.5, fontWeight: 700, color: tk.text2, padding: 0,
          '&:hover': { color: tk.text },
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5" /><path d="M11 18l-6-6 6-6" />
        </svg>
        {backLabel}
      </Box>

      {/* ------------------------------- header ------------------------------- */}
      {/* flexWrap stays 'wrap' outside mobilePortrait (landscape/tablet/
          desktop stay as-is) — but in portrait, avatar (62px) + text stack
          (minWidth: 260) together don't fit one line, so that same wrap was
          dropping the text below the avatar instead of keeping it beside
          it. 'nowrap' + the text stack's minWidth: 0 below is what actually
          keeps them side by side. */}
      <Box
        sx={{
          background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: `${radii.header}px`,
          p: '18px 20px', display: 'flex', gap: '16px', alignItems: 'center',
          flexWrap: mobilePortrait ? 'nowrap' : 'wrap',
        }}
      >
        <Box
          sx={{
            width: mobilePortrait ? 48 : 62, height: mobilePortrait ? 48 : 62, borderRadius: '16px', flex: 'none', display: 'grid', placeItems: 'center',
            color: '#fff', fontWeight: 800, fontSize: mobilePortrait ? 16 : 19, overflow: 'hidden',
            background: isImageCriteria ? tk.surface3 : 'linear-gradient(135deg,#2D89F4,#7C5CFF)',
          }}
        >
          {isImageCriteria ? (
            // The actual uploaded photo (face_search/photo_searcher/irbis_eye/
            // image_insize all ship it as `criteria`, a data:image base64
            // string) instead of the "UP" initials of the literal placeholder
            // text "uploaded image" — that text is still what's used for the
            // H1/meta line above, just not here where a real thumbnail reads
            // far better. Picked up by the sample-result blur rule above like
            // every other <img> in this view.
            <Box component="img" src={row.criteria} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : headerPhoto ? (
            // buildSummary() harvests every photo URL anywhere in the payload
            // (see adapters/summary.ts's `IMG_KEYS`) regardless of workflow
            // type, so this is the same "first WORKING photo found" behaviour
            // across Reddit, Snapchat, combined_name, etc. — not
            // template-specific. `headerPhoto` skips any URL already known
            // dead (see `brokenHeaderPhotos` above); `onError` below adds one
            // to that set and re-renders with the next candidate instead of
            // leaving a broken-image icon in place of the initials.
            <Box
              component="img"
              src={headerPhoto}
              alt=""
              onError={() => setBrokenHeaderPhotos((prev) => (prev.has(headerPhoto) ? prev : new Set(prev).add(headerPhoto)))}
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : /* initials only read well for names; a phone or IP becomes "97", so show a glyph instead */
          /[a-z]/i.test(String(summary?.title ?? '')) ? (
            initials(String(summary?.title ?? title))
          ) : (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <circle cx="12" cy="9" r="3.4" />
              <path d="M4.5 20c1-3.6 4-5.4 7.5-5.4S18.5 16.4 19.5 20" />
            </svg>
          )}
        </Box>

        {/*
          Text stack top-aligned with the avatar (rather than the row's
          default vertical centering) specifically for the image case below,
          where dropping the H1 leaves only two short lines — centered, that
          block floated oddly low against the 62px-tall photo. `alignSelf:
          'center'` for every other case keeps their unchanged look (roughly
          as tall as the avatar already, so centering vs. top barely differs).
        */}
        <Box sx={{ minWidth: 0, flex: 1, alignSelf: isImageCriteria ? 'flex-start' : 'center' }}>
          {/* The raw criteria is a data:image string here, so the H1 would
              just show its "uploaded image" placeholder — the actual photo
              is already the avatar to the left, so this line is dropped
              entirely for image results instead of repeating that text. */}
          {!isImageCriteria && (
            <ClickAwayListener onClickAway={() => setCriteriaHintOpen(false)}>
              <Tooltip
                title={summary?.title || criteria || title}
                open={criteriaHintOpen}
                onClose={() => setCriteriaHintOpen(false)}
                disableFocusListener
                disableHoverListener
                disableTouchListener
              >
                <Typography
                  component="h1"
                  onClick={() => setCriteriaHintOpen((v) => !v)}
                  sx={{
                    fontSize: 20, fontWeight: 800, letterSpacing: '-.02em', color: tk.text,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer',
                  }}
                >
                  {/* The subject name resolved from the result data (buildSummary's
                      `h.names[0]`, harvested the same way regardless of workflow
                      type) leads here, falling back to the raw search criteria the
                      user typed/uploaded only when no name was found. The "N
                      source(s) returned data" pill used to sit here too, but that's
                      already covered in more detail by the "Result quality" card in
                      the sticky rail (see ResultQuality below), so it's dropped here
                      rather than shown twice. Truncated with an ellipsis when too
                      long (e.g. a full crypto wallet address) — click/tap to see
                      the full value in a tooltip. */}
                  {summary?.title || criteria || title}
                </Typography>
              </Tooltip>
            </ClickAwayListener>
          )}

          {/* Harvested facts (phone carrier, e-mails found, breaches, ...)
              when there are any; otherwise the workflow's own name (e.g.
              "ui_pep") rather than repeating the H1's criteria a second
              time — see buildSummary's header comment on `answer`. Dropped
              entirely on a portrait phone — mobilePortrait's meta line
              below already carries the technical fields (type/date/status)
              and there isn't room for this too. */}
          {!mobilePortrait && (
            <Box sx={{ fontSize: 13, color: tk.text2, mt: '5px' }}>
              {summary && summary.answer.length > 0 ? (
                summary.answer.map((a, i) => (
                  <Box component="span" key={i}>
                    {i > 0 && <Box component="span" sx={{ color: tk.text3, mx: '7px' }}>·</Box>}
                    <Box component="span" sx={{ color: tk.text, fontWeight: 600 }}>{a[0]}</Box>
                    {a[1] && <Box component="span" sx={{ color: tk.text3, ml: '6px', fontSize: 11.5 }}>{a[1]}</Box>}
                  </Box>
                ))
              ) : (
                <Box component="span" sx={{ color: tk.text, fontWeight: 600 }}>{title}</Box>
              )}
            </Box>
          )}

          {mobilePortrait ? (
            // Just the technical fields — type, date, status — one per
            // line rather than the desktop's dot-joined single line, which
            // reads cramped once the harvested-facts line above is gone
            // and this is carrying everything on its own.
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, mt: 0, color: tk.text3, fontSize: 11.5 }}>
              <span>{title}</span>
              {row.createdTime && <span>{fmtDate(row.createdTime)}</span>}
              {row.status && <span>{`Status: ${row.status}`}</span>}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', mt: '7px', color: tk.text3, fontSize: 11.5 }}>
              {[
                // The workflow title already moved up into the line above when
                // there were no harvested facts to show instead — don't repeat
                // it here too in that case.
                summary && summary.answer.length > 0 ? title : null,
                // Raw search criteria, now that the H1 above leads with the
                // resolved subject name instead — only shown when it adds
                // information beyond what's already in the H1 (a bare id
                // rather than the actual name is worth surfacing here).
                criteria && criteria !== (summary?.title || criteria) ? criteria : null,
                row.createdTime ? fmtDate(row.createdTime) : null,
                row.status ? `Status: ${row.status}` : null,
              ]
                .filter((item): item is string => !!item)
                .map((item, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <Box component="span" sx={{ color: tk.text3, mx: '7px' }}>·</Box>}
                    <span>{item}</span>
                  </React.Fragment>
                ))}
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: '7px', flexWrap: 'wrap' }}>
          {adminActions}
          {!inline && (
            <IconButton ref={headerCloseRef} onClick={close} aria-label="close" sx={{ ml: '4px' }}>
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Floating close button — every screen size, and only once the
          header's own close button (tracked via headerCloseRef above) has
          scrolled out of view, so there's never two visible at once. Fixed
          to the viewport, so it stays reachable no matter how far down a
          long result the user has scrolled. zIndex 20: above ordinary page
          content, still well under Drawer's 1300 (see Header.tsx's own
          sticky bar for that same ceiling). */}
      {!inline && !headerCloseVisible && (
        <IconButton
          onClick={close}
          aria-label="close"
          size="small"
          sx={{
            position: 'fixed', top: 12, right: scrollbarWidth + 13, zIndex: 20,
            width: 26, height: 26, background: tk.surface, border: `1px solid ${tk.border}`,
            boxShadow: '0 4px 14px rgba(0,0,0,.25)',
            '&:hover': { background: tk.surface3 },
          }}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      )}

      {/* -------------------------------- body -------------------------------- */}
      <Box
        sx={{
          display: 'grid', gap: '14px', mt: '14px',
          gridTemplateColumns: narrow ? '1fr' : 'minmax(0,1fr) 274px',
          // No `alignItems: 'start'` override — the default `stretch` lets
          // this row grow to fill the `flex: 1` height below (see the outer
          // Box's comment) and stretches the main column to match, which is
          // what lets a short NoData panel grow the rest of the way down to
          // the footer instead of stopping at its own natural height. Only
          // makes a visible difference when there's real leftover space to
          // fill (inline route has none, and a normally-tall result already
          // reaches the row's height on its own).
          //
          // Deliberately no `minHeight: 0` here — that forces a flex item to
          // shrink below its own content size, and long template content
          // (e.g. a full text/report body) then overflowed this box instead
          // of pushing the page taller, leaving the footer floating over the
          // middle of the spilled-over text instead of sitting below it.
          // Left at the default `min-height: auto`, this can still grow to
          // fill leftover space, but never shrinks below what its content
          // actually needs.
          ...(inline ? {} : { flex: 1 }),
        }}
      >
        <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {/* Still running AND nothing has come back yet — the template
              would have nothing real to show, so this replaces it entirely
              (same reasoning as NoData) until either data lands or the poll
              effect above lands a finished/failed/empty status. Once at
              least one provider has data, the template renders that data
              immediately instead (see hasPartialData above) and the
              floating ProgressToast below takes over signalling "still
              searching". */}
          {row.status === 'progress' && !hasPartialData ? (
            <InProgress percentage={row.percentage} />
          ) : (
            <Template apiData={apiData} mapSlot={mapSlot} />
          )}
        </Box>

        <Box sx={{ position: narrow ? 'static' : 'sticky', top: 16, display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {providers && <ResultQuality summary={providers} createdTime={row.createdTime} />}
        </Box>
      </Box>

      {/* Floating "still searching" notice — only once there's already
          partial data on screen (see hasPartialData above); with nothing to
          show yet, InProgress above already covers that full-panel. */}
      {row.status === 'progress' && hasPartialData && <ProgressToast percentage={row.percentage} />}
    </Box>
  )

  if (inline) return body

  return (
    <Dialog
      open
      onClose={close}
      fullScreen
      PaperProps={{ sx: { background: tk.bg }, ref: paperRef }}
    >
      {body}
    </Dialog>
  )
}

/* --------------------------------------------------------------------- */

/**
 * Result quality — replaces the old "IRBI insight" placeholder.
 *
 * That card duplicated the Ask IRBI edge tab sitting right beside it and said
 * nothing. This one answers the question a user actually has when a result
 * looks thin: how much of it is real, and what did not come back.
 */
function ResultQuality({ summary, createdTime }: { summary: ProviderSummary; createdTime?: string }) {
  const tk = useTokens()
  const total = summary.declared || 1
  const pct = Math.round((summary.withData.length / total) * 100)
  const tone = pct >= 60 ? '#10B981' : pct >= 30 ? '#F59E0B' : '#EF4444'

  const rows: [string, React.ReactNode, string?][] = [
    ['Returned data', `${summary.withData.length} of ${summary.declared}`, tone],
    ['Answered empty', summary.emptyCount],
    ['Failed', summary.failedCount, summary.failedCount ? '#EF4444' : undefined],
  ]
  if (summary.duplicatesDropped) rows.push(['Duplicates merged', summary.duplicatesDropped])
  if (createdTime) {
    const d = new Date(createdTime)
    if (!isNaN(d.getTime())) rows.push(['Retrieved', d.toLocaleString()])
  }

  return (
    <Box sx={{ background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: `${radii.card}px`, p: '14px 15px' }}>
      <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.08em', color: tk.text3, fontWeight: 700, mb: '11px' }}>
        Result quality
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px', mb: '4px' }}>
        <Typography sx={{ fontSize: 26, fontWeight: 800, color: tone, letterSpacing: '-.02em' }}>{pct}%</Typography>
        <Typography sx={{ fontSize: 11.5, color: tk.text3 }}>source coverage</Typography>
      </Box>
      <Box sx={{ height: 5, borderRadius: '20px', background: tk.surface3, overflow: 'hidden', mb: '12px' }}>
        <Box sx={{ height: '100%', width: `${pct}%`, borderRadius: '20px', background: tone }} />
      </Box>

      <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 12px', m: 0, fontSize: 12 }}>
        {rows.map(([k, v, c], i) => (
          <React.Fragment key={i}>
            <Box component="dt" sx={{ color: tk.text3 }}>{k}</Box>
            <Box component="dd" sx={{ m: 0, fontWeight: 600, textAlign: 'right', color: c || tk.text }}>{v}</Box>
          </React.Fragment>
        ))}
      </Box>

      <Typography sx={{ fontSize: 10.5, color: tk.text3, mt: '11px', lineHeight: 1.45 }}>
        {summary.failedCount > 0
          ? 'Some providers failed for this search.'
          : 'Coverage depends on which providers hold data for this identifier.'}
      </Typography>
    </Box>
  )
}
